// Her Space - Women-first space for connection and community
// Two tabs: Channels (rooms) and Circles (scheduled events)

import React, { useEffect, useState, useContext, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Heart, Shield, Users, Sparkles, ChevronRight,
  MessageCircle, BookOpen, Lock, ArrowLeft, Loader2,
  Star, Compass, Calendar, Clock, CheckCircle
} from 'lucide-react';
import { toast } from 'sonner';
import apiClient from "../api/client";
import { AuthContext } from '../App';
import { GlobalNav } from '../components/GlobalNav';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import HerSpaceHub from '../components/HerSpaceHub';

// Channel definitions mapping to hub cards
const HER_SPACE_CHANNELS = [
  { key: "sister_wives", title: "Sister Wives & Family Building", description: "For women exploring plural family dynamics, communication, and partnership growth.", icon: Users },
  { key: "boundaries", title: "Boundaries & Consent", description: "Honest conversations about needs, limits, and navigating complex relationship dynamics.", icon: Shield },
  { key: "healing", title: "Healing & Healthy Pacing", description: "A softer space for processing emotions, past experiences, and building healthy patterns.", icon: Heart },
  { key: "mothers", title: "Mothers & Family Life", description: "For mothers navigating parenting, family decisions, and multi-partner households.", icon: Star },
  { key: "culture", title: "Culture & Community", description: "Celebrating our roots, traditions, and building community across experiences.", icon: Sparkles },
  { key: "new_here", title: "New Here", description: "Just arriving? Start here. Ask questions, introduce yourself, find your footing.", icon: Compass },
];

function formatDate(dt) {
  if (!dt) return '';
  const d = new Date(dt);
  return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
}

function formatTime(dt) {
  if (!dt) return '';
  const d = new Date(dt);
  return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
}

// ── Circles List Sub-component ──────────────────────────
function CirclesTab({ token }) {
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [topicTags, setTopicTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');
  const [rsvpLoading, setRsvpLoading] = useState(null);

  const headers = { Authorization: `Bearer ${token}` };

  const fetchCircles = useCallback(async () => {
    try {
      const params = filter ? `?topic_tag=${filter}` : '';
      const res = await apiClient.get(`/her-space/circles${params}`, { headers });
      setEvents(res.data.events || []);
      setTopicTags(res.data.topic_tags || []);
    } catch (err) {
      console.error('Failed to fetch circles', err);
    } finally {
      setLoading(false);
    }
  }, [token, filter]);

  useEffect(() => { fetchCircles(); }, [fetchCircles]);

  const handleRSVP = async (eventId, status) => {
    setRsvpLoading(eventId);
    try {
      const res = await apiClient.post(`/her-space/circles/${eventId}/rsvp`, { rsvp_status: status }, { headers });
      setEvents(prev => prev.map(ev =>
        ev.id === eventId ? { ...ev, viewer_rsvp: status, rsvp_count: res.data.rsvp_count } : ev
      ));
      toast.success(status === 'going' ? "You're going!" : 'RSVP updated');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to RSVP');
    } finally {
      setRsvpLoading(null);
    }
  };

  if (loading) {
    return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-rose-500" /></div>;
  }

  return (
    <div>
      {/* Topic filters */}
      {topicTags.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-5" data-testid="circle-topic-filters">
          <button onClick={() => setFilter('')}
            className={`px-3 py-1.5 text-xs font-medium rounded-full transition ${
              !filter ? 'bg-rose-600 text-white' : 'bg-gray-100 dark:bg-slate-800 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-slate-700'
            }`} data-testid="filter-all">
            All
          </button>
          {topicTags.map(tag => (
            <button key={tag.key} onClick={() => setFilter(tag.key)}
              className={`px-3 py-1.5 text-xs font-medium rounded-full transition ${
                filter === tag.key ? 'bg-rose-600 text-white' : 'bg-gray-100 dark:bg-slate-800 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-slate-700'
              }`} data-testid={`filter-${tag.key}`}>
              {tag.label}
            </button>
          ))}
        </div>
      )}

      {/* Events */}
      {events.length === 0 ? (
        <div className="text-center py-12" data-testid="no-circles-message">
          <Calendar className="w-12 h-12 mx-auto mb-3 text-gray-300 dark:text-gray-600" />
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">No upcoming circles right now</p>
          <p className="text-xs text-gray-400 dark:text-gray-500">Check back soon for new events!</p>
        </div>
      ) : (
        <div className="space-y-3" data-testid="circles-list">
          {events.map(ev => {
            const isGoing = ev.viewer_rsvp === 'going';
            const isLive = ev.computed_status === 'live';

            return (
              <div key={ev.id}
                className={`surface-card rounded-2xl p-4 sm:p-5 shadow-sm transition-all hover:shadow-md cursor-pointer ${
                  isLive ? 'ring-2 ring-green-300 dark:ring-green-500/40' : ''
                }`}
                onClick={() => navigate(`/her-space/circles/${ev.id}`)}
                data-testid={`circle-card-${ev.id}`}>
                <div className="flex items-start gap-4">
                  <div className={`w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 ${
                    isLive ? 'bg-green-100 dark:bg-green-900/40' : 'bg-rose-50 dark:bg-rose-900/30'
                  }`}>
                    <Calendar className={`w-5 h-5 ${isLive ? 'text-green-600 dark:text-green-400' : 'text-rose-600 dark:text-rose-400'}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      {isLive && (
                        <span className="flex items-center gap-1 px-2 py-0.5 text-xs font-semibold rounded-full bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300 animate-pulse">
                          Live
                        </span>
                      )}
                      <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-rose-50 dark:bg-rose-900/30 text-rose-700 dark:text-rose-300">
                        {ev.topic_label}
                      </span>
                    </div>
                    <h3 className="font-medium text-gray-900 dark:text-white mb-1 text-sm sm:text-base">{ev.title}</h3>
                    <div className="flex flex-wrap gap-3 text-xs text-gray-500 dark:text-gray-400">
                      <span className="flex items-center gap-1"><Calendar className="w-3 h-3" />{formatDate(ev.start_at)}</span>
                      <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{formatTime(ev.start_at)}</span>
                      <span className="flex items-center gap-1"><Users className="w-3 h-3" />{ev.rsvp_count} going</span>
                    </div>
                  </div>
                  <div className="flex-shrink-0" onClick={(e) => e.stopPropagation()}>
                    {isGoing ? (
                      <span className="flex items-center gap-1.5 px-3 py-2 text-xs font-medium rounded-xl bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300">
                        <CheckCircle className="w-3.5 h-3.5" />
                        Going
                      </span>
                    ) : (
                      <button onClick={() => handleRSVP(ev.id, 'going')}
                        disabled={rsvpLoading === ev.id}
                        className="flex items-center gap-1.5 px-3 py-2 text-xs font-medium rounded-xl bg-rose-600 text-white hover:bg-rose-700 transition disabled:opacity-50"
                        data-testid={`rsvp-${ev.id}`}>
                        {rsvpLoading === ev.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Heart className="w-3.5 h-3.5" />}
                        RSVP
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ── Channels Sub-component ──────────────────────────────
function ChannelsTab({ token, defaultChannel, setDefaultChannel, savingPreference, setSavingPreference }) {
  const navigate = useNavigate();
  const [activeHub, setActiveHub] = useState(null); // { key, label }

  const handleExploreChannel = useCallback((channelKey) => {
    const channel = HER_SPACE_CHANNELS.find(c => c.key === channelKey);
    setActiveHub({ key: channelKey, label: channel?.title || channelKey });
  }, []);

  const handleEnterRoom = useCallback((channelKey) => {
    navigate(`/rooms/her_space_private?channel=${channelKey}&from=her_space_hub`);
  }, [navigate]);

  const handleSetDefaultChannel = async (channelKey) => {
    try {
      setSavingPreference(true);
      await apiClient.patch(`/rooms/her-space/preference`, { channel: channelKey }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDefaultChannel(channelKey);
      toast.success("Default channel updated!");
    } catch (err) {
      toast.error("Failed to save preference");
    } finally {
      setSavingPreference(false);
    }
  };

  if (activeHub) {
    return (
      <HerSpaceHub
        activeChannelKey={activeHub.key}
        activeChannelLabel={activeHub.label}
        onBack={() => setActiveHub(null)}
      />
    );
  }

  return (
    <div>
      {/* Primary Action Card */}
      <div className="surface-card rounded-2xl p-5 shadow-sm mb-5">
        <div className="flex items-start gap-4">
          <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-rose-100 to-purple-100 dark:from-rose-900/40 dark:to-purple-900/40 flex items-center justify-center flex-shrink-0">
            <Sparkles className="w-5 h-5 text-rose-600 dark:text-rose-400" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-gray-600 dark:text-gray-300 text-sm leading-relaxed mb-3">
              One room, many vibes. Explore channels or jump into the general conversation.
            </p>
            <button onClick={() => {
                const el = document.getElementById('her-space-channel-list');
                if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
              }}
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-rose-500 to-purple-500 text-white rounded-xl font-medium text-sm hover:from-rose-600 hover:to-purple-600 transition-all shadow-sm hover:shadow-md"
              data-testid="browse-rooms-btn">
              <Heart className="w-4 h-4" />
              Browse Rooms
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Info Row */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-6">
        <div className="surface-card rounded-2xl p-4 shadow-sm">
          <div className="flex items-center gap-2.5 mb-2.5">
            <div className="w-7 h-7 rounded-lg bg-blue-50 dark:bg-blue-900/30 flex items-center justify-center">
              <BookOpen className="w-3.5 h-3.5 text-blue-600 dark:text-blue-400" />
            </div>
            <h3 className="font-medium text-gray-900 dark:text-white text-sm">How It Works</h3>
          </div>
          <ul className="space-y-1.5 text-xs text-gray-600 dark:text-gray-400">
            <li className="flex items-start gap-2"><span className="text-rose-500">-</span>Join rooms that resonate with you</li>
            <li className="flex items-start gap-2"><span className="text-rose-500">-</span>Share, listen, and connect</li>
            <li className="flex items-start gap-2"><span className="text-rose-500">-</span>No pressure, no expiring content</li>
          </ul>
        </div>
        <div className="surface-card rounded-2xl p-4 shadow-sm">
          <div className="flex items-center gap-2.5 mb-2.5">
            <div className="w-7 h-7 rounded-lg bg-amber-50 dark:bg-amber-900/30 flex items-center justify-center">
              <Shield className="w-3.5 h-3.5 text-amber-600 dark:text-amber-400" />
            </div>
            <h3 className="font-medium text-gray-900 dark:text-white text-sm">Guidelines</h3>
          </div>
          <ul className="space-y-1.5 text-xs text-gray-600 dark:text-gray-400">
            <li className="flex items-start gap-2"><span className="text-amber-500">-</span>Respect boundaries and consent</li>
            <li className="flex items-start gap-2"><span className="text-amber-500">-</span>Support without judgment</li>
            <li className="flex items-start gap-2"><span className="text-amber-500">-</span>What's shared stays here</li>
          </ul>
        </div>
      </div>

      {/* Channel Cards */}
      <div id="her-space-channel-list" className="flex items-center justify-between mb-3">
        <h2 className="text-sm font-semibold text-gray-900 dark:text-white flex items-center gap-2">
          <Compass className="w-4 h-4 text-rose-500 dark:text-rose-400" />
          Explore Channels
        </h2>
        <span className="text-xs text-gray-400">{HER_SPACE_CHANNELS.length} vibes</span>
      </div>
      <div className="space-y-3">
        {HER_SPACE_CHANNELS.map((channel) => {
          const IconComponent = channel.icon;
          const isDefault = defaultChannel === channel.key;
          return (
            <div key={channel.key}
              className={`surface-card rounded-2xl p-4 shadow-sm transition-all hover:shadow-md ${isDefault ? "ring-2 ring-rose-200 dark:ring-rose-500/30" : ""}`}
              data-testid={`channel-card-${channel.key}`}>
              <div className="flex items-start gap-3">
                <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 ${isDefault ? "bg-rose-100 dark:bg-rose-900/40" : "bg-gray-100 dark:bg-slate-800"}`}>
                  <IconComponent className={`w-4 h-4 ${isDefault ? "text-rose-600 dark:text-rose-400" : "text-gray-500 dark:text-gray-400"}`} />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-medium text-gray-900 dark:text-white text-sm mb-0.5">{channel.title}</h3>
                  <p className="text-xs text-gray-600 dark:text-gray-400 line-clamp-2">{channel.description}</p>
                  {isDefault && <p className="text-xs text-rose-600 dark:text-rose-400 mt-1 font-medium">Your default</p>}
                </div>
                <div className="flex-shrink-0 flex gap-2">
                  {!isDefault && (
                    <button onClick={() => handleSetDefaultChannel(channel.key)} disabled={savingPreference}
                      className="p-2 text-gray-400 hover:text-rose-500 transition disabled:opacity-50" title="Set as default"
                      data-testid={`set-default-${channel.key}`}>
                      <Star className="w-4 h-4" />
                    </button>
                  )}
                  <button onClick={() => handleExploreChannel(channel.key)}
                    className="flex items-center gap-1.5 px-3 py-2 text-xs font-medium bg-rose-600 text-white rounded-xl hover:bg-rose-700 transition"
                    data-testid={`explore-${channel.key}`}>
                    Explore
                    <ChevronRight className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Main Page ───────────────────────────────────────────
export default function HerSpacePage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { token } = useContext(AuthContext);
  const [loading, setLoading] = useState(true);
  const [accessStatus, setAccessStatus] = useState(null);
  const [defaultChannel, setDefaultChannel] = useState("general");
  const [savingPreference, setSavingPreference] = useState(false);

  const activeTab = searchParams.get('tab') || 'today';

  useEffect(() => {
    const checkAccess = async () => {
      if (!token) {
        setAccessStatus({ allowed: false, reason: "not_logged_in" });
        setLoading(false);
        return;
      }
      try {
        const res = await apiClient.get(`/her-space/status`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setAccessStatus(res.data);
        if (res.data.allowed) {
          try {
            const prefRes = await apiClient.get(`/rooms/her-space/preference`, {
              headers: { Authorization: `Bearer ${token}` }
            });
            setDefaultChannel(prefRes.data.default_channel || "general");
          } catch { /* default */ }
        }
      } catch (err) {
        if (err.response?.status === 403) {
          setAccessStatus({ allowed: false, reason: "not_woman" });
        } else {
          setAccessStatus({ allowed: false, reason: "error" });
        }
      } finally {
        setLoading(false);
      }
    };
    checkAccess();
  }, [token]);

  if (loading) {
    return (
      <div className="min-h-screen surface-page">
        <GlobalNav />
        <div className="flex items-center justify-center py-32">
          <Loader2 className="w-8 h-8 animate-spin text-rose-500 dark:text-rose-400" />
        </div>
      </div>
    );
  }

  if (!accessStatus?.allowed) {
    return (
      <div className="min-h-screen surface-page">
        <GlobalNav />
        <div className="max-w-md mx-auto px-4 py-16 text-center">
          <div className="w-20 h-20 rounded-full bg-rose-100 dark:bg-rose-900/30 flex items-center justify-center mx-auto mb-6">
            <Lock className="w-10 h-10 text-rose-500 dark:text-rose-400" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Her Space</h1>
          <p className="text-gray-600 dark:text-gray-400 mb-6">Her Space is a private sanctuary for women.</p>
          <p className="text-sm text-gray-500 dark:text-gray-500 mb-8 leading-relaxed">
            This is a women-first space for grounding, learning, and real talk.
            If you believe this is an error, please update your profile.
          </p>
          <button onClick={() => navigate("/dashboard")}
            className="px-6 py-3 bg-gray-100 dark:bg-slate-800 text-gray-900 dark:text-white rounded-2xl hover:bg-gray-200 dark:hover:bg-slate-700 transition font-medium"
            data-testid="her-space-back-btn">
            Back to Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen surface-page">
      <GlobalNav />
      <div className="max-w-3xl mx-auto px-4 py-6 pb-24">
        {/* Header */}
        <header className="mb-6">
          <button onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 transition mb-4"
            data-testid="back-button">
            <ArrowLeft className="w-4 h-4" />
            <span className="text-sm">Back</span>
          </button>
          <div className="flex items-center gap-3 mb-1">
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Her Space</h1>
            <span className="px-2.5 py-1 text-xs font-medium rounded-full bg-rose-100 dark:bg-rose-900/40 text-rose-700 dark:text-rose-300">
              Safe Space
            </span>
          </div>
          <p className="text-gray-600 dark:text-gray-400 text-sm">
            A women-first space for grounding, learning, and real talk.
          </p>
        </header>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={(v) => setSearchParams({ tab: v })} className="w-full">
          <TabsList className="w-full mb-5 bg-gray-100 dark:bg-slate-800 p-1 rounded-xl" data-testid="her-space-tabs">
            <TabsTrigger value="today" className="flex-1 rounded-lg data-[state=active]:bg-white dark:data-[state=active]:bg-slate-700 data-[state=active]:shadow-sm text-sm"
              data-testid="tab-today">
              <Sparkles className="w-4 h-4 mr-1.5" />
              Today
            </TabsTrigger>
            <TabsTrigger value="community" className="flex-1 rounded-lg data-[state=active]:bg-white dark:data-[state=active]:bg-slate-700 data-[state=active]:shadow-sm text-sm"
              data-testid="tab-community">
              <MessageCircle className="w-4 h-4 mr-1.5" />
              Community
            </TabsTrigger>
            <TabsTrigger value="circles" className="flex-1 rounded-lg data-[state=active]:bg-white dark:data-[state=active]:bg-slate-700 data-[state=active]:shadow-sm text-sm"
              data-testid="tab-circles">
              <Calendar className="w-4 h-4 mr-1.5" />
              Circles
            </TabsTrigger>
          </TabsList>

          <TabsContent value="today">
            <div className="space-y-4" data-testid="today-tab-content">
              <div className="surface-card rounded-2xl p-5 shadow-sm border-l-4 border-rose-400 dark:border-rose-500">
                <div className="text-xs font-bold uppercase tracking-wider text-rose-600 dark:text-rose-400 mb-2">
                  Today's Prompt
                </div>
                <p className="text-sm text-gray-900 dark:text-white font-medium leading-relaxed mb-2">
                  What's been sitting with you today?
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-500 mb-4">
                  Take a moment. No rush, no pressure. Just honesty.
                </p>
                <button
                  onClick={() => navigate('/inner-work')}
                  className="inline-flex items-center gap-2 px-5 py-2.5 bg-slate-900 dark:bg-white text-white dark:text-slate-900 rounded-xl font-medium text-sm hover:opacity-90 transition"
                  data-testid="write-reflection-btn"
                >
                  <BookOpen className="w-4 h-4" />
                  Write a Reflection
                </button>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="community">
            <ChannelsTab
              token={token}
              defaultChannel={defaultChannel}
              setDefaultChannel={setDefaultChannel}
              savingPreference={savingPreference}
              setSavingPreference={setSavingPreference}
            />
          </TabsContent>

          <TabsContent value="circles">
            <CirclesTab token={token} />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
